"use client"

import * as React from "react"
import Link from "next/link"
import Image from "next/image"
import { usePathname } from "next/navigation"
import { ServiceItem } from "@/lib/types/cms"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet"
import {
    NavigationMenu,
    NavigationMenuContent,
    NavigationMenuItem,
    NavigationMenuLink,
    NavigationMenuList,
    NavigationMenuTrigger,
    navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu"
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion"
import { Menu, Phone, ChevronDown, Home, Info, Globe, FileText, Briefcase, CheckCircle2, GraduationCap } from "lucide-react"

interface PublicNavProps {
    services: ServiceItem[]
}

const mainNavigation = [
    { name: "Anasayfa", href: "/", icon: Home },
    { name: "Hakkımızda", href: "/hakkimizda", icon: Info },
    { name: "Ülkeler", href: "/ulkeler", icon: Globe },
    { name: "Blog", href: "/blog", icon: FileText },
    { name: "İletişim", href: "/iletisim", icon: Phone },
]

export function PublicNav({ services }: PublicNavProps) {
    const pathname = usePathname()
    const [isOpen, setIsOpen] = React.useState(false)

    const visibleServices = services.filter((service) => {
        const name = service.name?.toLowerCase() || ""
        const slug = service.slug?.toLowerCase() || ""
        return !slug.includes("schengen") && !name.includes("schengen")
    })
    const featuredServices = visibleServices.slice(0, 5)

    return (
        <header className="sticky top-0 z-50 w-full border-b border-primary/10 bg-green-50/95 backdrop-blur supports-[backdrop-filter]:bg-green-50/80">
            <div className="container flex h-20 items-center justify-between">
                {/* Logo */}
                <Link href="/" className="flex items-center">
                    <div className="relative h-16 w-48">
                        <Image
                            src="/logo.png"
                            alt="Riyo Vize Danışmanlık Logo - İzmir Vize Danışmanlık"
                            fill
                            sizes="(max-width: 768px) 150px, 192px"
                            className="object-contain"
                            priority
                        />
                    </div>
                </Link>

                {/* Desktop Nav */}
                <div className="hidden md:flex items-center gap-1">
                    <NavigationMenu>
                        <NavigationMenuList>
                            <NavigationMenuItem>
                                <NavigationMenuLink asChild className={cn(navigationMenuTriggerStyle(), pathname === "/" && "text-primary font-bold bg-primary/5")}>
                                    <Link href="/">
                                        Anasayfa
                                    </Link>
                                </NavigationMenuLink>
                            </NavigationMenuItem>

                            <NavigationMenuItem>
                                <NavigationMenuTrigger className={cn(pathname.startsWith("/hizmetler") && "text-primary font-bold bg-primary/5")}>
                                    Hizmetlerimiz
                                </NavigationMenuTrigger>
                                <NavigationMenuContent>
                                    <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
                                        {/* Highlighted Item */}
                                        <li className="row-span-3">
                                            <NavigationMenuLink asChild>
                                                <Link
                                                    className="flex h-full w-full select-none flex-col justify-between rounded-md bg-gradient-to-b from-primary/50 to-primary p-6 no-underline outline-none focus:shadow-md group relative overflow-hidden"
                                                    href="/basvuru"
                                                >
                                                    <div className="bg-white/20 p-2 w-fit rounded-full backdrop-blur-sm group-hover:scale-110 transition-transform">
                                                        <CheckCircle2 className="h-8 w-8 text-white" />
                                                    </div>
                                                    <div className="mt-4">
                                                        <div className="mb-2 text-lg font-medium text-white">
                                                            Ücretsiz Ön Değerlendirme
                                                        </div>
                                                        <p className="text-sm leading-tight text-white/90">
                                                            Uzman ekibimizle vize şansınızı hemen analiz edin.
                                                        </p>
                                                    </div>
                                                </Link>
                                            </NavigationMenuLink>
                                        </li>
                                        {featuredServices.map((service) => (
                                            <ListItem
                                                key={service.slug}
                                                title={service.name}
                                                href={`/hizmetler/${service.slug}`}
                                            >
                                                {service.short_description?.slice(0, 50)}...
                                            </ListItem>
                                        ))}
                                        <ListItem title="Denklik" href="/hizmetler/denklik-danismanligi" className="bg-primary/5">
                                            Mezuniyet denklik işlemlerinizi uzman ekibimizle tamamlayın.
                                        </ListItem>
                                        <ListItem title="Tüm Hizmetler" href="/hizmetler" className="bg-muted/50 font-bold">
                                            Hizmetler sayfasını incele →
                                        </ListItem>
                                    </ul>
                                </NavigationMenuContent>
                            </NavigationMenuItem>

                            {mainNavigation.slice(1).map((item) => (
                                <NavigationMenuItem key={item.href}>
                                    <NavigationMenuLink asChild className={cn(navigationMenuTriggerStyle(), pathname === item.href && "text-primary font-bold bg-primary/5")}>
                                        <Link href={item.href}>
                                            {item.name}
                                        </Link>
                                    </NavigationMenuLink>
                                </NavigationMenuItem>
                            ))}
                        </NavigationMenuList>
                    </NavigationMenu>
                </div>

                {/* Desktop Actions */}
                <div className="hidden md:flex items-center gap-4">
                    <Button variant="ghost" className="gap-2" asChild>
                        <Link href="tel:+905331391444">
                            <Phone className="h-4 w-4" />
                            <span>Destek</span>
                        </Link>
                    </Button>
                    <Button asChild className="bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg transition-all">
                        <Link href="/basvuru">Ücretsiz Ön Değerlendirme</Link>
                    </Button>
                </div>

                {/* Mobile Nav */}
                <Sheet open={isOpen} onOpenChange={setIsOpen}>
                    <SheetTrigger asChild>
                        <Button variant="ghost" size="icon" className="md:hidden">
                            <Menu className="h-6 w-6" />
                            <span className="sr-only">Menüyü aç</span>
                        </Button>
                    </SheetTrigger>
                    <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                        <div className="flex flex-col h-full mt-6">
                            <Link href="/" onClick={() => setIsOpen(false)} className="flex items-center space-x-2 font-bold text-xl text-primary mb-8 px-2">
                                Riyo Vize Danışmanlık
                            </Link>

                            <div className="flex-1 overflow-y-auto pr-2">
                                <nav className="flex flex-col space-y-1">
                                    <Link
                                        href="/"
                                        onClick={() => setIsOpen(false)}
                                        className={cn(
                                            "flex items-center gap-3 py-3 px-3 text-lg font-medium transition-colors hover:bg-muted rounded-md",
                                            pathname === "/" ? "text-primary bg-primary/5 border-l-4 border-primary" : "text-foreground"
                                        )}
                                    >
                                        <Home className="h-5 w-5" />
                                        Anasayfa
                                    </Link>

                                    <Accordion type="single" collapsible className="w-full">
                                        <AccordionItem value="services" className="border-none">
                                            <AccordionTrigger className="py-3 px-3 text-lg font-medium hover:bg-muted hover:no-underline rounded-md flex items-center gap-3">
                                                <span className="flex items-center gap-3">
                                                    <Briefcase className="h-5 w-5" />
                                                    Hizmetlerimiz
                                                </span>
                                            </AccordionTrigger>
                                            <AccordionContent>
                                                <div className="flex flex-col space-y-1 pl-4 border-l-2 border-muted ml-2 mt-1">
                                                    {visibleServices.map((service) => (
                                                        <Link
                                                            key={service.slug}
                                                            href={`/hizmetler/${service.slug}`}
                                                            onClick={() => setIsOpen(false)}
                                                            className={cn(
                                                                "py-2 px-2 text-sm font-medium transition-colors hover:text-primary block",
                                                                pathname === `/hizmetler/${service.slug}` && "text-primary"
                                                            )}
                                                        >
                                                            {service.name}
                                                        </Link>
                                                    ))}
                                                    <Link
                                                        href="/hizmetler/denklik-danismanligi"
                                                        onClick={() => setIsOpen(false)}
                                                        className={cn(
                                                            "py-2 px-2 text-sm font-medium transition-colors hover:text-primary block",
                                                            pathname === "/hizmetler/denklik-danismanligi" && "text-primary"
                                                        )}
                                                    >
                                                        Denklik
                                                    </Link>
                                                    <Link
                                                        href="/hizmetler"
                                                        onClick={() => setIsOpen(false)}
                                                        className="py-2 px-2 text-sm font-bold text-primary block mt-2"
                                                    >
                                                        Tümünü Gör →
                                                    </Link>
                                                </div>
                                            </AccordionContent>
                                        </AccordionItem>
                                    </Accordion>

                                    {mainNavigation.slice(1).map((item) => (
                                        <Link
                                            key={item.href}
                                            href={item.href}
                                            onClick={() => setIsOpen(false)}
                                            className={cn(
                                                "flex items-center gap-3 py-3 px-3 text-lg font-medium transition-colors hover:bg-muted rounded-md",
                                                pathname === item.href ? "text-primary bg-primary/5 border-l-4 border-primary" : "text-foreground"
                                            )}
                                        >
                                            <item.icon className="h-5 w-5" />
                                            {item.name}
                                        </Link>
                                    ))}
                                </nav>
                            </div>

                            <div className="pt-6 mt-auto pb-6 border-t space-y-3">
                                <Button asChild className="w-full bg-primary text-white shadow-lg" size="lg">
                                    <Link href="/basvuru" onClick={() => setIsOpen(false)}>Hemen Başvur</Link>
                                </Button>
                                <div className="grid grid-cols-2 gap-2">
                                    <Button variant="outline" className="w-full" asChild>
                                        <Link href="tel:+905331391444">
                                            <Phone className="mr-2 h-4 w-4" />
                                            Ara
                                        </Link>
                                    </Button>
                                    <Button variant="secondary" className="w-full bg-[#25D366] text-white hover:bg-[#25D366]/90" asChild>
                                        <Link href="https://wa.me/905331391444" target="_blank">
                                            WhatsApp
                                        </Link>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </SheetContent>
                </Sheet>
            </div>
        </header>
    )
}

const ListItem = React.forwardRef<
    React.ElementRef<"a">,
    React.ComponentPropsWithoutRef<"a">
>(({ className, title, children, ...props }, ref) => {
    return (
        <li>
            <NavigationMenuLink asChild>
                <a
                    ref={ref}
                    className={cn(
                        "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
                        className
                    )}
                    {...props}
                >
                    <div className="text-sm font-medium leading-none">{title}</div>
                    <p className="line-clamp-2 text-sm leading-snug text-muted-foreground mt-1.5">
                        {children}
                    </p>
                </a>
            </NavigationMenuLink>
        </li>
    )
})
ListItem.displayName = "ListItem"
